/**
 * @atlas
 * @kind client
 * @partOf primitive:ui, primitive:websockets
 * @uses primitive:shared
 */
import { useQueryClient } from '@tanstack/react-query';
import { parseChannelKey } from '@zealot/shared/ws';
import { type ApiWebsocket, createApiWebsocket } from '@zealot/ui/lib/ws/createApiWebsocket';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react';

type ApiWebsocketContextValue = {
  subscribe: (channel: string) => () => void;
};

const ApiWebsocketContext = createContext<ApiWebsocketContextValue | null>(null);

type ApiWebsocketProviderProps = {
  children: ReactNode;
  getToken: () => Promise<string | null | undefined>;
  baseUrl?: string;
};

const WEBSOCKET_PATH = '/ws/v2';

export const toWebsocketUrl = (baseUrl: string): string =>
  `${baseUrl.replace(/\/+$/, '').replace(/^http/, 'ws')}${WEBSOCKET_PATH}`;

// gloss
export const ApiWebsocketProvider = ({ children, getToken, baseUrl }: ApiWebsocketProviderProps) => {
  const queryClient = useQueryClient();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const live = useMemo<{ websocket: ApiWebsocket; value: ApiWebsocketContextValue } | null>(() => {
    if (!baseUrl) return null;

    const refetchChannels = (channels: string[]): void => {
      for (const channel of channels) {
        void queryClient.invalidateQueries({ queryKey: [parseChannelKey(channel)] });
      }
    };

    const websocket = createApiWebsocket(
      toWebsocketUrl(baseUrl),
      queryClient,
      () => getTokenRef.current(),
      refetchChannels,
    );

    const value: ApiWebsocketContextValue = {
      subscribe: (channel) => {
        let isHeld = true;
        let isSubscribed = false;
        websocket.connect();
        void websocket.syncIdentity().then(() => {
          if (!isHeld) return;
          websocket.subscribe(channel);
          isSubscribed = true;
        });
        return () => {
          isHeld = false;
          if (isSubscribed) websocket.unsubscribe(channel);
        };
      },
    };
    return { websocket, value };
  }, [baseUrl, queryClient]);

  useEffect(() => () => live?.websocket.close(), [live]);

  return <ApiWebsocketContext.Provider value={live?.value ?? null}>{children}</ApiWebsocketContext.Provider>;
};

// gloss
export const useLiveChannel = (channel: string | null | undefined): void => {
  const websocket = useContext(ApiWebsocketContext);
  useEffect(() => {
    if (!websocket || !channel) return;
    return websocket.subscribe(channel);
  }, [websocket, channel]);
};
